import { failure, ok, pending, Result } from "../common/common";
import { ApiService, isBadRequestError } from "../api";
import { TMessage } from "../i18n/tt";
import { makeAutoObservable } from "mobx";
import { navigateToTransaction } from "../utils/navigation";
import UserStore from "./UserStore";
import { History } from "history";
import { ShareTransferForm } from "../shareholderRegister/transactions/shareTransfer/shareTransferDomain";
import { IncreaseNominalValueTransactionModel } from "../shareholderRegister/transactions/increaseNominalValue/increaseNominalValueDomain";
import { CancellationOfSharesModel } from "../shareholderRegister/transactions/cancellationOfShares/cancellationOfSharesDomain";
import { ConversionFromISINModel } from "../shareholderRegister/transactions/conversionFromISIN/conversionFromISINDomain";
import { SplitModel } from "../shareholderRegister/transactions/split/splitDomain";
import { ReverseSplitModel } from "../shareholderRegister/transactions/reverseSplit/reverseSplitDomain";
import { ReductionNominalValueForm } from "../shareholderRegister/transactions/reductionOfNominalValue/reductionOfNominalValueDomain";
import { GroupContributionForm } from "../shareholderRegister/transactions/groupContribution/groupContributionDomain";
import { DividendForm } from "../shareholderRegister/transactions/dividend/dividendDomain";
import { ConversionOfSharesModel } from "../shareholderRegister/transactions/conversionOfShares/conversionOfSharesDomain";
import { LiquidationForm } from "../shareholderRegister/transactions/liquidation/liquidationDomain";
import { ShareholdingPerDateModel } from "../shareholderRegister/transactions/shareholdingPerDate/shareholdingPerDateDomain";
import AlertMessageStore from "./AlertMessageStore";
import { TransactionType } from "../types/enums/TransactionType";
import { IncorporationTransactionModel } from "../shareholderRegister/transactions/issue/incorporationTransactionDomain";
import { IssueOfNewSharesTransactionModel } from "../shareholderRegister/transactions/issue/issueTransactionDomain";
import { ValidationError, validationErrorMessage } from "../utils/Errors";

export class TransactionStore {
  result?: Result<number, TMessage>;
  replayError?: ReplayTransactionError;

  constructor(
    readonly api: ApiService,
    readonly userStore: UserStore,
    readonly messageStore: AlertMessageStore,
    readonly history: History,
  ) {
    makeAutoObservable(this, { api: false, userStore: false, history: false }, { autoBind: true });
  }

  *incorporation(model: IncorporationTransactionModel) {
    this.result = undefined;
    if (!model.validate()) return;

    model.clearDirty();
    try {
      this.result = pending();
      const transactionId: number = yield this.api.issueShares(model.prepareData());
      this.showSuccessPage(transactionId);
    } catch (e) {
      this.result = failure({ key: "general-error" });
      this.messageStore.showError({ key: "general-error" });
    }
  }

  *showSuccessPage(transactionId: number) {
    yield this.userStore.updateCompanyInfo();
    this.result = ok(transactionId);
    navigateToTransaction(this.userStore.orgNumber, transactionId, this.history);
    this.messageStore.showSuccess({ key: "transactions.save-success" });
  }

  *issueShares(model: IssueOfNewSharesTransactionModel) {
    this.result = undefined;
    if (!model.validate()) return;

    model.clearDirty();
    try {
      this.result = pending();
      const transactionId: number = yield this.api.issueShares(model.prepareData());
      this.showSuccessPage(transactionId);
    } catch (e) {
      this.result = failure({ key: "general-error" });
      this.messageStore.showError({ key: "general-error" });
    }
  }

  *increaseNominalValue(model: IncreaseNominalValueTransactionModel) {
    this.result = undefined;
    if (!model.validate()) return;

    model.clearDirty();
    try {
      this.result = pending();
      const transactionId: number = yield this.api.increaseNominalValue(model.prepareData());
      this.showSuccessPage(transactionId);
    } catch (e) {
      this.result = failure({ key: "general-error" });
      this.messageStore.showError({ key: "general-error" });
    }
  }

  *shareTransfer(model: ShareTransferForm, existingTransactionId?: string) {
    this.result = undefined;
    if (!model.validate()) return;

    model.clearDirty();
    try {
      this.result = pending();
      const transactionId: number = model.isSellAllShares
        ? yield this.api.transferAllShares(model.prepareDataSellAll(), existingTransactionId)
        : yield this.api.createTransfer(model.prepareDataNewTransaction(), existingTransactionId);
      this.showSuccessPage(transactionId);
    } catch (e) {
      this.result = failure({ key: "general-error" });
      if (isBadRequestError(e) && e.response?.data) {
        const error = e.response.data as TransactionErrorResponse;
        if (error.replayError !== undefined) {
          this.replayError = error.replayError;
        }
      } else {
        this.messageStore.showError({ key: "general-error" });
      }
    }
  }

  *cancelShares(model: CancellationOfSharesModel) {
    this.result = undefined;
    if (!model.validate()) return;
    model.clearDirty();
    try {
      this.result = pending();
      const transactionId: number = yield this.api.shareCancellation(
        model.prepareDataCancellationOfShares(),
      );
      this.showSuccessPage(transactionId);
    } catch (e) {
      this.result = failure({ key: "general-error" });
      this.messageStore.showError({ key: "general-error" });
    }
  }

  *split(model: SplitModel, existingTransactionId?: string) {
    this.result = undefined;
    if (!model.validate()) return;
    model.clearDirty();
    try {
      this.result = pending();
      const transactionId: number = yield this.api.shareSplit(
        model.prepareDataShareSplit(),
        existingTransactionId,
      );
      this.showSuccessPage(transactionId);
    } catch (e) {
      this.result = failure({ key: "general-error" });
      if (isBadRequestError(e) && e.response?.data) {
        const error = e.response.data as TransactionErrorResponse;
        if (error.replayError !== undefined) {
          this.replayError = error.replayError;
        } else if (error.validationError !== undefined) {
          this.messageStore.showError(validationErrorMessage[error.validationError]);
        }
      } else {
        this.messageStore.showError({ key: "general-error" });
      }
    }
  }

  *conversionFromISIN(model: ConversionFromISINModel) {
    this.result = undefined;
    if (!model.validate()) return;
    model.clearDirty();
    try {
      this.result = pending();
      const transactionId: number = yield this.api.conversionFromISIN(model.prepareData());
      this.showSuccessPage(transactionId);
    } catch (e) {
      this.result = failure({ key: "general-error" });
      this.messageStore.showError({ key: "general-error" });
    }
  }

  *reverseSplit(model: ReverseSplitModel, existingTransactionId?: string) {
    this.result = undefined;
    if (!model.validate()) return;
    model.clearDirty();
    try {
      this.result = pending();
      const transactionId: number = yield this.api.shareReverseSplit(
        model.prepareReverseSplitData(),
        existingTransactionId,
      );
      this.showSuccessPage(transactionId);
    } catch (e) {
      this.result = failure({ key: "general-error" });
      if (isBadRequestError(e) && e.response?.data) {
        const error = e.response.data as TransactionErrorResponse;
        if (error.replayError !== undefined) {
          this.replayError = error.replayError;
        }
      } else {
        this.messageStore.showError({ key: "general-error" });
      }
    }
  }

  *reduceNominalValue(model: ReductionNominalValueForm) {
    this.result = undefined;
    if (!model.validate()) return;
    model.clearDirty();
    try {
      this.result = pending();
      const transactionId: number = yield this.api.reductionOfNominalValue(
        model.prepareDataReductionOfNominalValue(),
      );
      this.showSuccessPage(transactionId);
    } catch (e) {
      this.result = failure({ key: "general-error" });
      this.messageStore.showError({ key: "general-error" });
    }
  }

  *groupContribution(model: GroupContributionForm) {
    this.result = undefined;
    if (!model.validate()) return;
    model.clearDirty();
    try {
      this.result = pending();
      const transactionId: number = yield this.api.registerGroupContribution(
        model.prepareDataGroupContribution(),
      );
      this.showSuccessPage(transactionId);
    } catch (e) {
      this.result = failure({ key: "general-error" });
      this.messageStore.showError({ key: "general-error" });
    }
  }

  *dividend(model: DividendForm) {
    this.result = undefined;
    if (!model.validate()) return;
    model.clearDirty();
    try {
      this.result = pending();
      const transactionId: number = yield this.api.payDividend(model.prepareDataPayDividend());
      this.showSuccessPage(transactionId);
    } catch (e) {
      this.result = failure({ key: "general-error" });
      this.messageStore.showError({ key: "general-error" });
    }
  }

  *conversionOfShares(model: ConversionOfSharesModel) {
    this.result = undefined;
    if (!model.validate()) return;
    model.clearDirty();
    try {
      this.result = pending();
      const transactionId: number = yield this.api.conversionOfShares(model.prepareData());
      this.showSuccessPage(transactionId);
    } catch (e) {
      this.result = failure({ key: "general-error" });
      this.messageStore.showError({ key: "general-error" });
    }
  }

  *liquidation(model: LiquidationForm) {
    this.result = undefined;
    if (!model.validate()) return;
    model.clearDirty();
    try {
      this.result = pending();
      const transactionId: number = yield this.api.liquidation(model.prepareData());
      this.showSuccessPage(transactionId);
    } catch (e) {
      this.result = failure({ key: "general-error" });
      this.messageStore.showError({ key: "general-error" });
    }
  }

  *shareholdingPerDate(model: ShareholdingPerDateModel) {
    this.result = undefined;
    if (!model.validate()) return;
    model.clearDirty();
    try {
      this.result = pending();
      const transactionId: number = yield this.api.shareholdingPerDate(model.prepareData());
      this.showSuccessPage(transactionId);
    } catch (e) {
      this.result = failure({ key: "general-error" });
      this.messageStore.showError({ key: "general-error" });
    }
  }

  clearReplayError() {
    this.replayError = undefined;
  }
}

export type TransactionErrorResponse = {
  validationError?: ValidationError;
  replayError?: ReplayTransactionError;
};

export type ReplayTransactionError = {
  transactionId: number;
  transactionType: TransactionType;
  transactionDate: string;
};
